import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react'
import { Modal, ModalStatus, ReusableModalProps } from './Modal'

interface ModalContextProps {
  open: boolean
  status: ModalStatus
  message?: string
  openModal: (modalStatus?: ModalStatus) => void
  closeModal: () => void
  setStatus: (props: { status: ModalStatus; message?: string }) => void
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined)

export const useCustomModal = (): ModalContextProps => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error(
      'The useCustomModal hook is only available under ModalProvider.'
    )
  }

  return context
}

export type ModalProviderProps = Omit<
  ReusableModalProps,
  'children' | 'footer'
> & {
  children?: (props: ModalContextProps) => ReactNode
  footer?: (props: ModalContextProps) => ReactNode
  modalContent?: React.ReactNode
}

export function ModalProvider({
  children,
  footer,
  open: vOpen = false,
  modalContent,
  ...restProps
}: ModalProviderProps) {
  const [open, setVisible] = useState(vOpen)

  const [status, setStatus] = useState<{
    status: ModalStatus
    message?: string
  }>({ status: '' })

  const openModal = useCallback(
    (modalStatus: ModalStatus = '', message?: string) => {
      setStatus({ status: modalStatus, message })
      setVisible(true)
    },
    []
  )

  const closeModal = useCallback(() => {
    setVisible(false)
  }, [])

  return (
    <ModalContext.Provider
      value={{ open, status: status.status, openModal, closeModal, setStatus }}
    >
      {typeof children === 'function'
        ? children({
            open,
            status: status.status,
            openModal,
            closeModal,
            setStatus,
          })
        : children}
      <Modal
        footer={
          typeof footer === 'function'
            ? footer({
                open,
                status: status.status,
                openModal,
                closeModal,
                setStatus,
              })
            : footer
        }
        open={open}
        status={status.status}
        message={status.message}
        onClose={closeModal}
        {...restProps}
      >
        {modalContent}
      </Modal>
    </ModalContext.Provider>
  )
}
